import * as React from "react";
import { Popup } from "@progress/kendo-react-popup";
import { GridCellProps } from "@progress/kendo-react-grid";
import "./CPEllipsisMenuCellStyles.scss";

export type CPMenuItem = string | { label: string; icon?: string };

interface NormalizedMenuItem {
  label: string;
  icon?: string;
}

function normalizeItems(items: CPMenuItem[]): NormalizedMenuItem[] {
  return items.map((item) =>
    typeof item === "string" ? { label: item } : item,
  );
}

export interface CPEllipsisMenuCellProps extends GridCellProps {
  onMenuOpen: (dataItem: any) => CPMenuItem[];
  onMenuItemClick?: (label: string, dataItem: any) => void;
}

const CPEllipsisMenuCell: React.FC<CPEllipsisMenuCellProps> = (props) => {
  const { dataItem, onMenuOpen, onMenuItemClick } = props;
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<NormalizedMenuItem[]>([]);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        !anchorRef.current?.contains(e.target as Node) &&
        !popupRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!open) {
      setItems(normalizeItems(onMenuOpen(dataItem)));
    }
    setOpen((prev) => !prev);
  };

  const handleItemClick = (e: React.MouseEvent, label: string) => {
    e.stopPropagation();
    setOpen(false);
    onMenuItemClick?.(label, dataItem);
  };

  return (
    <td
      style={props.style}
      role="gridcell"
      colSpan={1}
      className="cp-ellipsis-menu-cell"
    >
      <button
        ref={anchorRef}
        className="cp-ellipsis-btn"
        onClick={handleButtonClick}
        title="Actions"
      >
        &#8942;
      </button>
      <Popup
        anchor={anchorRef.current}
        show={open}
        popupClass="cp-ellipsis-popup"
        animate={{ openDuration: 100, closeDuration: 100 }}
      >
        <div ref={popupRef} className="cp-ellipsis-menu">
          {items.map((item) => (
            <div
              key={item.label}
              className="cp-ellipsis-menu-item"
              onClick={(e) => handleItemClick(e, item.label)}
            >
              {item.icon && (
                <i className={`${item.icon} cp-ellipsis-menu-item-icon`} />
              )}
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </Popup>
    </td>
  );
};

export default CPEllipsisMenuCell;
